// eslint-disable-next-line no-restricted-imports -- dialog previews must not suspend: the confirm control stays disabled and the retry stays inside the dialog while the breakdown loads or fails
import { useQuery } from '@tanstack/react-query';
import type { EndpointDef } from '@ooh/contracts';
import type { z } from 'zod';
import { useApiClient } from '../plugs/api-client';
import { apiQueryOptions } from './use-api-suspense-query';

interface ApiQueryOptions<TResponse, TData> {
  enabled?: boolean;
  staleTime?: number;
  /** Replaces the default `[method, path, request]` key so the entry joins an existing key tree. */
  queryKey?: readonly unknown[];
  /** Runs inside the query, so a response it cannot map becomes a query error instead of a render crash. */
  select?: (response: TResponse) => TData;
  /** The caller renders its own error state, so the global error toast would duplicate it. */
  suppressGlobalErrorToast?: boolean;
}

export function useApiQuery<TDef extends EndpointDef, TData = z.output<TDef['responseSchema']>>(
  def: TDef,
  request: z.input<TDef['requestSchema']>,
  {
    enabled = true,
    staleTime,
    queryKey,
    select,
    suppressGlobalErrorToast = false,
  }: ApiQueryOptions<z.output<TDef['responseSchema']>, TData> = {},
) {
  return useQuery({
    ...apiQueryOptions(useApiClient(), def, request),
    ...(queryKey ? { queryKey } : {}),
    enabled,
    staleTime,
    select,
    meta: suppressGlobalErrorToast ? { suppressGlobalErrorToast: true } : undefined,
  });
}
